import {
  CalendarCheck,
  Filter,
  Droplets,
  Snowflake,
  Sparkles,
  ClipboardList,
  Clock,
  ListChecks,
  Bell,
} from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import PageHero from "@/components/site/PageHero";
import ServiceFAQ from "@/components/site/ServiceFAQ";
import RelatedServices from "@/components/site/RelatedServices";
import FinalCTA from "@/components/site/FinalCTA";
import AreaPills from "@/components/site/AreaPills";
import DemoNotice from "@/components/site/DemoNotice";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/51ddf5f6-e80c-4259-9ef6-c5393e7b5903/images/6c0f367cca2954e37cc43df12dabc27ab5ae3d2225313ab53fc49411ef2a492c.png";

const WHEN = [
  {
    icon: CalendarCheck,
    title: "Pre-summer cooling check",
    desc: "Early spring is the right time — before the system has to run all day, every day.",
  },
  {
    icon: CalendarCheck,
    title: "Fall mid-season check",
    desc: "A quick second look after the hardest months. Catches anything that's drifted.",
  },
  {
    icon: Filter,
    title: "Regular filter changes",
    desc: "Every 60–90 days for most homes — sooner with pets, dust, or active remodeling.",
  },
  {
    icon: Droplets,
    title: "Condensate drain flush",
    desc: "Florida humidity makes drain line backups a real concern. Worth doing on schedule.",
  },
  {
    icon: Snowflake,
    title: "Coil cleaning",
    desc: "Indoor and outdoor coils both lose efficiency as they collect dust and salt residue.",
  },
  {
    icon: Sparkles,
    title: "Tune-up vs. emergency repair",
    desc: "Maintenance catches small problems before they turn into a hot-Saturday phone call.",
  },
];

const INCLUDED = [
  "Filter inspection and replacement",
  "Condensate drain line flush",
  "Float switch test",
  "Indoor evaporator coil rinse",
  "Outdoor condenser coil rinse",
  "Refrigerant level and pressure check",
  "Electrical: capacitors, contactor, wire connections",
  "Thermostat calibration and battery check",
  "Airflow at supply vents and return",
];

const TELL_US = [
  {
    icon: Clock,
    title: "When you last had a tune-up (if ever)",
    desc: "Rough timing is fine — last spring, two years ago, never since we moved in.",
  },
  {
    icon: ListChecks,
    title: "Anything you've noticed lately",
    desc: "Higher bills, weaker cooling in one room, longer run cycles, musty smells, etc.",
  },
  {
    icon: Bell,
    title: "Whether you want filter reminders",
    desc: "Optional — automatic SMS reminders for filter changes between visits.",
  },
];

const FAQS = [
  {
    q: "How often should I schedule HVAC maintenance in Florida?",
    a: "For Southwest Florida, twice a year is the practical answer: a pre-summer tune-up and a fall mid-season check. Filters get changed every 60–90 days for most homes, and condensate drain lines should be flushed regularly to avoid backups during peak season.",
  },
  {
    q: "What does a tune-up actually include?",
    a: "A full tune-up looks at airflow, coils (indoor and outdoor), refrigerant pressures, electrical components, the condensate drain, the float switch, the thermostat, and the filter. The goal is to catch worn parts and lost efficiency before they become a service call.",
  },
  {
    q: "How often should I change my air filter?",
    a: "Most SWFL homes do well with a filter change every 60–90 days. Homes with pets, allergies, or recent construction may want to change more often. A clogged filter is one of the most common reasons systems start cooling poorly.",
  },
  {
    q: "Why is the condensate drain line so important here?",
    a: "Florida humidity pulls a lot of moisture out of the air, and that water has to go somewhere. If the drain line clogs — common with algae buildup — water backs up into the pan and can shut the system down (via the float switch) or cause real damage if it overflows.",
  },
  {
    q: "Do you offer a maintenance plan?",
    a: "This demo site captures interest in recurring maintenance plans — the actual plan structure, frequency, and pricing is something a real contractor would build out. The intake here lets homeowners flag that interest so it doesn't get lost.",
  },
  {
    q: "Is this a real HVAC company?",
    a: 'Sun Chaser Cooling is a sample HVAC website created by PrismForge Digital. <a class="text-aqua-deep underline-offset-2 hover:underline" href="/demo">See how this demo works →</a>',
    html: true,
  },
];

export default function Maintenance() {
  useSeo({
    title:
      "HVAC Maintenance & Tune-Ups in Southwest Florida | Sun Chaser Cooling — PrismForge Digital Demo",
    description:
      "Demo HVAC maintenance page for SWFL homes. Seasonal tune-ups, drain line flushes, filter swaps, and a maintenance plan concept — built by PrismForge Digital.",
  });

  return (
    <div data-testid="maintenance-page">
      <PageHero
        eyebrow="Maintenance & Tune-Ups"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Services", to: "/services" },
          { label: "Maintenance" },
        ]}
        title="HVAC maintenance & tune-ups"
        highlight="for Florida heat."
        subtitle="Pre-summer tune-ups, drain line flushes, filter swaps, and a maintenance plan concept built around how Florida systems actually run."
        primaryCta={{
          label: "Schedule Maintenance",
          to: "/request-service?intent=maintenance",
        }}
        secondaryCta={{ label: "Request HVAC Service", to: "/request-service" }}
        image={HERO_IMG}
        imageAlt="Replacing a clean HVAC air filter — part of seasonal AC maintenance in Southwest Florida."
        variant="light"
        testId="maintenance-hero"
      />

      {/* When to schedule */}
      <section
        data-testid="maintenance-when-section"
        aria-labelledby="maintenance-when-heading"
        className="relative bg-white py-20 sm:py-24"
      >
        <div className="container-page">
          <div className="max-w-2xl">
            <span className="pill">
              <CalendarCheck className="h-3 w-3 text-aqua" />
              When to schedule
            </span>
            <h2
              id="maintenance-when-heading"
              className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
            >
              Six moments maintenance pays off.
            </h2>
            <p className="mt-4 text-base text-navy/70 sm:text-lg">
              Maintenance isn&rsquo;t one big yearly thing &mdash; it&rsquo;s a
              few small, well-timed things.
            </p>
          </div>
          <ul className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {WHEN.map((w) => {
              const Icon = w.icon;
              const slug = w.title
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/(^-|-$)/g, "");
              return (
                <li
                  key={w.title}
                  data-testid={`maintenance-when-${slug}`}
                  className="rounded-2xl border border-mist-line bg-white p-6 shadow-soft transition-transform hover:-translate-y-1"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-navy text-aqua">
                    <Icon className="h-4 w-4" />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-bold leading-tight tracking-tight text-navy">
                    {w.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-navy/70">
                    {w.desc}
                  </p>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* What's included */}
      <section
        data-testid="maintenance-included-section"
        aria-labelledby="maintenance-included-heading"
        className="relative bg-mist py-20 sm:py-24"
      >
        <div className="container-page grid grid-cols-1 items-start gap-12 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-5">
            <span className="pill">
              <ListChecks className="h-3 w-3 text-aqua" />
              What&rsquo;s included
            </span>
            <h2
              id="maintenance-included-heading"
              className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
            >
              A real tune-up checklist.
            </h2>
            <p className="mt-4 max-w-lg text-base text-navy/70 sm:text-lg">
              Not a 10-minute glance &mdash; the actual list of things
              that should be checked twice a year on a Florida HVAC system.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="rounded-2xl border border-mist-line bg-white shadow-soft">
              {INCLUDED.map((line, i) => (
                <li
                  key={line}
                  className={`flex items-center justify-between gap-3 px-5 py-4 ${
                    i < INCLUDED.length - 1 ? "border-b border-mist-line" : ""
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-aqua/15 font-display text-xs font-bold text-aqua-deep">
                      {i + 1}
                    </span>
                    <p className="text-[15px] font-medium text-navy/85">
                      {line}
                    </p>
                  </div>
                  <span className="hidden text-[10px] font-semibold uppercase tracking-[0.16em] text-navy/40 sm:inline">
                    Checked
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* What to tell us */}
      <section
        data-testid="maintenance-tell-section"
        aria-labelledby="maintenance-tell-heading"
        className="relative bg-white py-20 sm:py-24"
      >
        <div className="container-page">
          <div className="max-w-2xl">
            <span className="pill">
              <ClipboardList className="h-3 w-3 text-aqua" />
              What to tell us
            </span>
            <h2
              id="maintenance-tell-heading"
              className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
            >
              Quick context that makes the visit better.
            </h2>
          </div>
          <ul className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
            {TELL_US.map((t) => {
              const Icon = t.icon;
              return (
                <li
                  key={t.title}
                  className="flex flex-col rounded-2xl border border-mist-line bg-white p-6 shadow-soft"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-navy text-aqua">
                    <Icon className="h-4 w-4" />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-bold leading-tight tracking-tight text-navy">
                    {t.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-navy/70">
                    {t.desc}
                  </p>
                </li>
              );
            })}
          </ul>
          <div className="mt-8">
            <DemoNotice
              variant="dark"
              label="Maintenance plan interest is captured as part of the demo intake."
            />
          </div>
        </div>
      </section>

      {/* SWFL context */}
      <section
        data-testid="maintenance-swfl-section"
        aria-labelledby="maintenance-swfl-heading"
        className="relative bg-mist py-20 sm:py-24"
      >
        <div className="container-page max-w-4xl">
          <span className="pill">
            <span className="h-1.5 w-1.5 rounded-full bg-aqua" />
            Southwest Florida context
          </span>
          <h2
            id="maintenance-swfl-heading"
            className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
          >
            Maintenance hits different in a humid coastal climate.
          </h2>
          <div className="mt-5 space-y-4 text-base leading-relaxed text-navy/75 sm:text-lg">
            <p>
              Long cooling seasons and constant humidity mean SWFL systems do
              more total work in a year than systems further north. That extra
              workload shows up everywhere: faster filter loading, more drain
              line buildup, more wear on capacitors and contactors, and faster
              coil contamination from salt-influenced air.
            </p>
            <p>
              Twice-a-year maintenance isn&rsquo;t about being fancy &mdash;
              it&rsquo;s just how you keep a Florida system running at the
              efficiency you&rsquo;re paying for.
            </p>
          </div>
          <div className="mt-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/55">
              Tune-ups across SWFL
            </p>
            <AreaPills className="mt-3" />
          </div>
        </div>
      </section>

      <ServiceFAQ
        title="Common maintenance questions."
        subtitle="Quick answers for the things homeowners ask most."
        faqs={FAQS}
        testId="maintenance-faq"
      />

      <RelatedServices currentSlug="maintenance" />

      <FinalCTA
        title="Get on a maintenance rhythm that actually saves money."
        subtitle="Schedule a tune-up — and tell us if you'd like to hear about an ongoing maintenance plan."
        primaryCta={{
          label: "Schedule Maintenance",
          to: "/request-service?intent=maintenance",
          icon: CalendarCheck,
        }}
      />
    </div>
  );
}
